import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { watchReducer } from './watchReducer';
import { IWatches, IGetPaginatedWatches } from 'types';
import { WatchActionTypes } from './watchActions';
import {
	watchInitialState,
	WatchContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './watchContext';
import { getPaginatedWatches, deleteWatch } from 'backend/watches';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const WatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(watchReducer, watchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IWatches>, IGetPaginatedWatches>(
		isMounted.current ? getPaginatedWatches : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchBySerialNumber: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			isActive: state.isActive ? true : null,
			sortBySerialNumber:
				state.sortBy === 'SerialNumberA-Z' || state.sortBy === 'SerialNumberZ-A' ? state.sortBy : null,
			sortByRestaurantAddress:
				state.sortBy === 'RestaurantAddressA-Z' || state.sortBy === 'RestaurantAddressZ-A'
					? state.sortBy
					: null,
			sortByCustomerName:
				state.sortBy === 'CustomerNameA-Z' || state.sortBy === 'CustomerNameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: WatchActionTypes.SET_WATCHES,
				payload: {
					watches: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteWatch(state.IDForDeletion);
			const filteredWatches = state.watches.items.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: WatchActionTypes.SET_WATCHES,
				payload: {
					watches: {
						count: state.watches.count - 1,
						items: filteredWatches,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: WatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: WatchActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: WatchActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: WatchActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const toggleIsActive = () => dispatch({ type: WatchActionTypes.TOGGLE_IS_ACTIVE });

	const resetSelectedControls = () => dispatch({ type: WatchActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: WatchActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: WatchActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: WatchActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: WatchActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForFullInfo = (value: string) => {
		dispatch({
			type: WatchActionTypes.SET_ID_FOR_FULL_INFO,
			payload: {
				IDForFullInfo: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: WatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: WatchActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
				},
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<WatchContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				toggleIsActive,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForFullInfo,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				watches: state.watches,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				isActive: state.isActive,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForFullInfo: state.IDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</WatchContext.Provider>
	);
};
